import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MyErrorStateMatcher } from 'app/pages/login/login.component';
import { CognitoService } from 'app/core/http/services/cognito.service';
import { User } from 'app/core/interfaces/user';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'src-register-confirmation',
  templateUrl: './register-confirmation.component.html',
  styleUrls: ['./register-confirmation.component.scss'],
})
export class RegisterConfirmationComponent implements OnInit {
  public form: FormGroup;
  public loading = false;
  public user: any = {} as User;
  public email: string | undefined;
  public submitted = false;
  public successConfirmation = false;
  public title = 'Confirmar conta';

  matcher = new MyErrorStateMatcher();

  countdownActive: boolean = false;
  countdownSeconds: number = 60;
  buttonLabel: string = 'Reenviar Código';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cognitoService: CognitoService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      code: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.email = params['email'];
      if (this.email) {
        this.form.get('email')?.setValue(this.email);
        this.form.get('email')?.disable();
      }
    });
  }

  private getFriendlyErrorMessage(error: any): string {
    if (!error) return 'Ocorreu um erro desconhecido durante a confirmação.';

    if (error.name) {
      switch (error.name) {
        case 'CodeMismatchException':
          return 'Código de verificação inválido. Por favor, verifique o código e tente novamente.';
        case 'ExpiredCodeException':
          return 'Código de verificação expirado. Por favor, solicite um novo código.';
        case 'LimitExceededException':
          return 'Limite de tentativas excedido. Por favor, aguarde e tente novamente mais tarde.';
        case 'TooManyRequestsException':
          return 'Muitas solicitações em um curto período. Por favor, aguarde um momento.';
        case 'UserNotFoundException':
          return 'Nenhuma conta encontrada com este e-mail.';
        case 'NotAuthorizedException':
          return 'Esta conta já foi confirmada. Por favor, faça login.';
      }
    }

    if (error.message && error.message.includes('Network Error')) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
    }

    return error.message || 'Ocorreu um erro durante a confirmação. Por favor, tente novamente.';
  }

  openSnackBar(message: string, status: 'success' | 'error' | 'warning') {
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: status === 'success' ? 'snack-success' : status === 'error' ? 'snack-error' : 'snack-warning',
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl(uri).then(() => window.location.reload());
  }

  async reSendCode() {
    if (!this.countdownActive) {
      const email = this.email;
      if (!email) {
        this.openSnackBar('E-mail não encontrado. Por favor, recarregue a página.', 'error');
        return;
      }

      try {
        await this.cognitoService.reSendCode(email);
        this.openSnackBar('Código reenviado com sucesso! Verifique seu e-mail.', 'success');

        this.countdownActive = true;
        this.buttonLabel = `Reenviar Código (${this.countdownSeconds}s)`;

        const countdownInterval = setInterval(() => {
          this.countdownSeconds--;

          if (this.countdownSeconds === 0) {
            clearInterval(countdownInterval);
            this.countdownActive = false;
            this.countdownSeconds = 60;
            this.buttonLabel = 'Reenviar Código';
          } else {
            this.buttonLabel = `Reenviar Código (${this.countdownSeconds}s)`;
          }
        }, 1000);
      } catch (error) {
        console.error(error);
        this.openSnackBar(this.getFriendlyErrorMessage(error), 'error');
      }
    }
  }

  async confirmCode() {
    if (this.form.invalid) {
      this.openSnackBar('Por favor, preencha todos os campos corretamente.', 'error');
      return;
    }

    this.user = {
      email: this.email,
      code: this.form.get('code')?.value,
    };

    this.loading = true;
    this.submitted = false;
    this.successConfirmation = false;

    try {
      await this.cognitoService.confirmSignUp(this.user);

      this.submitted = true;
      this.successConfirmation = true;
      this.openSnackBar('Conta confirmada com sucesso! Redirecionando...', 'success');

      setTimeout(() => {
        this.router.navigate(['home']);
      }, 2000);
    } catch (error) {
      console.error(error);
      this.submitted = true;
      this.successConfirmation = false;
      this.openSnackBar(this.getFriendlyErrorMessage(error), 'error');
    } finally {
      this.loading = false;
    }
  }
}
